<template>
  <b-modal
    :id="id"
    centered
    scrollable
    :title="`Share ${ shareType[0] + shareType.slice(1).toLowerCase() }: ${ campaignName }`"
    modal-class="share-modal"
    data-test="creative-share-modal"
  >
    <div v-if="shareType === 'CAMPAIGN'">
      <p>Warning: Sharing the campaign will allow other users to edit the campaign.</p>
    </div>
    <div
      class="border rounded p-4 mb-3"
    >
      <label
        for="user-email-input"
        class="font-weight-bold fn-size-15"
      >
        Add User
      </label>
      <b-input-group>
        <b-form-input
          id="user-email-input"
          placeholder="Email Address"
          v-model="userEmail"
          type="email"
          :class="[!emailInSharedWithList ? '' : 'is-invalid']"
        />
        <template #append>
          <b-button
            data-test="add-user-email"
            variant="transparent"
            @click="addEmailToShareList"
            :disabled="isAddDisabled"
            :class="{ 'btn-loading black-loader': isAddButtonLoading }"
          >
            Add
          </b-button>
        </template>
      </b-input-group>
      <div
        style="font-size: 0.75rem;"
        class="mt-2"
      >
        <p
          v-if="emailInSharedWithList && !isAddButtonLoading"
          :class="{'invalid': emailInSharedWithList}"
        >
          The entered email is already in the "Share With" list below.
        </p>
        <p
          v-else-if="!emailInSharedWithList && !isAddButtonLoading"
        >
          Enter the email addresses of the people you wish to share the {{ shareType.toLowerCase() }} with.
        </p>
      </div>
    </div>
    <div
      v-if="isShareWithEmailsListLoading"
      class="loader mx-auto"
      style="height: 300px"
    />
    <div
      class="border rounded p-4"
      v-else-if="alreadySharedWithEmails.length > 0 || totalSharedWithEmails.length > 0"
    >
      <p
        class="font-weight-bold fn-size-15"
      >
        Share With
      </p>
      <div
        id="share-with-emails-list-container"
      >
        <ul
          id="share-with-emails-list"
          class="m-0 p-0"
        >
          <share-modal-emails-list-item
            v-for="(email, index) in totalSharedWithEmails"
            class="border share-with-emails-item"
            :class="{
              'rounded': totalSharedWithEmails.length === 1,
              'top-card': totalSharedWithEmails.length > 1 && index === 0,
              'bottom-card': totalSharedWithEmails.length > 1 && index === totalSharedWithEmails.length -1,
              'borderless-bottom': totalSharedWithEmails.length > 1 && index !== totalSharedWithEmails.length -1,
            }"
            :key="index"
            :index="index"
            :resend-enabled="!isSelfServiceCampaign && alreadySharedWithEmails.includes(email)"
            :campaign="campaign"
            :share-type="shareType"
            :creative="creative"
            :email="email"
            @removeEmail="removeEmail"
          />
        </ul>
      </div>
    </div>
    <template #modal-footer>
      <span
        v-if="shareType === 'SHOWCASE'"
        class="mr-auto"
      >
        {{ sharedCount }}/{{ feedTotalCount }} items will be shared.
      </span>
      <b-button
        id="share-modal-invite"
        variant="primary"
        size="sm"
        type="button"
        @click="inviteEmails($event)"
        :disabled="isInviteDisabled"
        :class="{ 'btn-loading': isInviteButtonLoading }"
      >
        Share
      </b-button>
    </template>
  </b-modal>
</template>
<script>
import signUtils from '@/core/signUtils';
import { mapActions, mapState } from 'vuex';
import * as shareTypes from '@/modules/share/store/types';
import * as contentTypes from '@/modules/content/store/types';

import ShareModalEmailsListItem from '@/modules/share/components/ShareModalEmailsListItem';

function initialState() {
  return {
    newUserForm: {
      firstName: '',
      lastName: '',
      companyName: '',
    },
    isShareWithEmailsListLoading: false,
    emailInSharedWithList: false,
    isAddButtonLoading: false,
    isInviteButtonLoading: false,
    userEmail: '',
    alreadySharedWithEmails: [],
    userShareList: [],
  };
}

export default {
  name: 'ShareModal',
  components: {
    ShareModalEmailsListItem,
  },
  props: {
    id: {
      type: String,
      default: 'modalSharing',
    },
    shareType: {
      type: String,
      default: '',
    },
  },
  data() {
    return initialState();
  },
  computed: {
    ...mapState('campaigns', [
      'campaign',
    ]),
    ...mapState('content', [
      'feedTotalCount',
      'sharedCount',
      'creative',
    ]),
    ...mapState('account', {
      sharedByFirstName: 'firstName',
      sharedByLastName: 'lastName',
    }),
    isAddDisabled() {
      return !this.isEmailValid() || this.isAddButtonLoading;
    },
    isInviteDisabled() {
      return this.userShareList.length === 0 || this.isInviteButtonLoading;
    },
    campaignName() {
      return this.campaign?.name || '';
    },
    totalSharedWithEmails() {
      return [...this.userShareList, ...this.alreadySharedWithEmails];
    },
    isSelfServiceCampaign() {
      return this.campaign?.type === 'SELF_SERVICE';
    },
  },
  methods: {
    ...mapActions('share', [
      shareTypes.FETCH_SHARE_WITH_EMAILS_LIST,
      shareTypes.UPDATE_SHARE_WITH_EMAILS_LIST,
      shareTypes.REMOVE_EMAIL_FROM_SHARE_WITH,
    ]),
    ...mapActions('content', [
      contentTypes.FETCH_SHARED_COUNT,
    ]),
    openModal() {
      this.loadData();
      this.$bvModal.show(this.id);
    },
    loadData() {
      this.getAlreadySharedWithEmails();
      if (this.shareType === 'SHOWCASE') this.FETCH_SHARED_COUNT();
    },
    resetData() {
      Object.assign(this.$data, initialState());
    },
    trimEmail() {
      this.userEmail = this.userEmail.trim();
    },
    isEmailValid() {
      return signUtils.isEmailValid(this.userEmail);
    },
    async getAlreadySharedWithEmails() {
      this.isShareWithEmailsListLoading = true;
      this.alreadySharedWithEmails = await this.FETCH_SHARE_WITH_EMAILS_LIST({ campaignId: this.campaign?.id, shareType: this.shareType });
      this.isShareWithEmailsListLoading = false;
    },
    addEmailToShareList() {
      this.isAddButtonLoading = true;
      this.trimEmail();
      if (this.totalSharedWithEmails.includes(this.userEmail)) {
        this.emailInSharedWithList = true;
        this.isAddButtonLoading = false;
        return;
      }

      this.userShareList.push(this.userEmail);
      this.emailInSharedWithList = false;
      this.userEmail = '';
      this.isAddButtonLoading = false;
    },
    removeEmail(email) {
      if (this.alreadySharedWithEmails.includes(email)) {
        this.REMOVE_EMAIL_FROM_SHARE_WITH({
          campaignId: this.campaign.id,
          shareType: this.shareType,
          body: {
            sharedWithEmail: email,
          },
        })
          .then(() => {
            const index = this.alreadySharedWithEmails.findIndex((existingEmail) => existingEmail === email);
            this.alreadySharedWithEmails.splice(index, 1);
          });
        return;
      }
      if (this.userShareList.includes(email)) {
        const index = this.userShareList.findIndex((existingEmail) => existingEmail === email);
        this.userShareList.splice(index, 1);
      }
    },
    async inviteEmails(bvModalEvent) {
      bvModalEvent.preventDefault();
      this.isInviteButtonLoading = true;

      this.UPDATE_SHARE_WITH_EMAILS_LIST({
        campaignId: this.campaign.id,
        body: {
          type: this.shareType,
          creativeKey: this.creative.creativeKey,
          sharedWithEmails: [...this.userShareList],
        },
      })
        .then(() => {
          [...this.userShareList].forEach((email) => {
            this.alreadySharedWithEmails.push(email);
          });
          this.userShareList = [];
        })
        .finally(() => {
          this.isInviteButtonLoading = false;
        });
    },
  },
  mounted() {
    this.$root.$on('bv::modal::hide', (bvEvent, modalId) => {
      if (modalId === this.id) {
        this.resetData();
      }
    });
  },
  destroyed() {
    this.$root.$off('bv::modal::show');
    this.$root.$off('bv::modal::hidden');
  },
};
</script>
<style scoped lang="scss">
.fn-size-15 {
font-size: 15px;
}
.black-loader::after {
  border: 2px solid #000;
}
#share-with-emails-list-container {
  min-height: 300px;
  max-height: 40vh;
  overflow: scroll;
}
.borderless-bottom {
  border-bottom: 0 solid transparent !important;
}
.top-card {
border-radius: 4px 4px 0 0;
margin: 0;
border-bottom: 0 solid transparent;
}
.bottom-card {
border-radius: 0 0 4px 4px;
margin: 0;
border-top: 0 solid transparent;
}
</style>
<style lang="scss">
.share-modal .modal-dialog {
    min-width: 600px;
}
</style>
